import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 20;
const POLL_MS = 2000;
const SUCCESS_COPY = {
  fiver_subscription: "Your plan is active and your 28 monthly credits are ready. Ka pai — go post your first advert!",
  wallet_topup: "Your wallet has been topped up. Ka pai!",
  boost: "Your advert is boosted to the top for the next hour.",
  banner: "Thanks! Your banner is paid and waiting for a quick check. We'll email you as soon as it's live — full refund if it isn't approved.",
  listing_fee: "Your markdown is now live on Markdowns. Ka pai!",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [kind, setKind] = useState("");
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    let n = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setKind(data.kind || ""); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* transient */ }
      n += 1; setAttempts(n);
      if (n >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16">
      <Card className="border-slate-200 shadow-lg">
        <CardContent className="py-10 text-center space-y-4">
          {state === "polling" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-emerald-800 animate-spin" />
              <h1 className="font-display text-2xl font-bold">Confirming your payment…</h1>
              <p className="text-slate-500 text-sm">Poll {attempts + 1} / {MAX_POLLS}</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-700" data-testid="payment-success-icon" />
              <h1 className="font-display text-3xl font-extrabold text-emerald-900" data-testid="payment-success-title">{kind === "fiver_subscription" ? "Welcome to the Fiver Deal!" : "Payment received!"}</h1>
              <p className="text-slate-600">{SUCCESS_COPY[kind] || SUCCESS_COPY.listing_fee}</p>
              <div className="flex gap-3 justify-center pt-2">
                <Button onClick={()=>navigate(kind === "banner" ? "/" : "/dashboard")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="back-to-dashboard">{kind === "banner" ? "Back to Markdowns" : "Back to dashboard"}</Button>
                <Link to="/"><Button variant="outline">View public feed</Button></Link>
              </div>
            </>
          )}
          {state === "failed" && (
            <>
              <XCircle className="w-14 h-14 mx-auto text-red-600" />
              <h1 className="font-display text-2xl font-bold">Payment not completed</h1>
              <p className="text-slate-600">The listing wasn&apos;t published. You can try again from your dashboard.</p>
              <Button onClick={()=>navigate("/dashboard")} className="bg-emerald-800 hover:bg-emerald-900">Back to dashboard</Button>
            </>
          )}
          {state === "timeout" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-slate-500" />
              <h1 className="font-display text-2xl font-bold">Still processing…</h1>
              <p className="text-slate-600">Stripe is taking longer than usual. Refresh in a minute, or check your dashboard.</p>
              <Button onClick={()=>navigate("/dashboard")} className="bg-emerald-800 hover:bg-emerald-900">Go to dashboard</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
